import { Source } from 'apiConnector/quote-loading';
import type { PersistedTab, PersistedTabsDescription } from 'apiConnector/tabs/tabs.model';
import type { IdType } from 'epic/common-form/ReloadFormRequest';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { ProcessingChain } from 'models/ProcessingChain';
import type {
  ProductVariation,
  ProductVariationValidSyntax,
  SelectedProductVariation,
} from 'models/productsReferential';
import { EssentialWorkspaceTabActions } from 'state/workspaces/essential/actions';
import { SecondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { SecondaryMarketPostTradeWorkspaceActions } from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.actions';
import { SecondaryMarketQuoteWorkspaceActions } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.actions';
import { SmartQuoteWorkspaceActions } from 'state/workspaces/smart-quote/actions';

import type { AddonManagementWorkspaceActions } from './addonManagement/addonManagement.actions';
import type { BatchWorkspaceActions } from './batch/batch.actions';
import type { DynamicWorkspaceTabActions } from './dynamic/dynamic.actions';
import type { QuoteWorkspaceType, TabsForWorkspace, WorkspaceType } from './model';
import type { StaticWorkspaceTabActions } from './static/static.actions';
import type { WorkspaceTabContentActions } from './tabContent/tabContent.actions';
import type { WorskpaceDashboardActions } from './workspaces/workspace.actions';

export type WorkspaceActions =
  | WorskpaceDashboardActions
  | EssentialWorkspaceTabActions
  | AddonManagementWorkspaceActions
  | StaticWorkspaceTabActions
  | DynamicWorkspaceTabActions
  | SecondaryMarketQuoteWorkspaceActions
  | SecondaryMarketBatchActions
  | SecondaryMarketPostTradeWorkspaceActions
  | BatchWorkspaceActions
  | SmartQuoteWorkspaceActions
  | WorkspacePersistedTabsApplied
  | WorkspaceResetTabCompleted
  | WorkspaceResetTabRequested
  | WorkspaceTabActivated
  | WorkspaceTabUnactivated
  | WorkspaceTabCloned
  | WorkspaceTabCloneCompleted
  | WorkspaceTabContentActions
  | WorkspaceTabCreated
  | WorkspaceAdvisoryTabCreated
  | WorkspaceTabQuoteRepriced
  | WorkspaceTabRemoved
  | WorkspaceTabRenamed
  | WorkspaceTabMoved
  | WorkspaceTabRepriceRequested
  | WorkspaceTabPersistRequested
  | WorkspaceTabsAllRemoved
  | WorkspaceTabsFetched
  | WorkspaceTabsFetchRequested
  | WorkspaceTabsPersisted
  | WorkspaceTabDeleteSessionRequested
  | WorkspaceTabsFetchFailed
  | WorkspaceTabFrozen
  | WorkspaceTabUnfrozen
  | WorkspaceTabSetBusy
  | WorkspaceQuoteLoaded
  | WorkspaceTabProductVariationSelected
  | WorkspaceTabClientSelected
  | WorkspaceTabProductSelectionSubmitted
  | WorkspaceRfqLoadRequested
  | WorkspaceSharableQuoteLoadRequested
  | SelectedWorkspaceProductSavedToRecentProducts
  | WorkspaceTabProductSelectionSubmitFailed
  | WorkspaceQuoteReloadRequested
  | WorkspaceQuoteReloaded;

export function workspaceTabCreated<W extends WorkspaceType>(
  id: string,
  workspace: W,
  tabType: TabsForWorkspace[W],
  tabName?: string,
  insertAtStart?: boolean,
) {
  return {
    type: 'WORKSPACE_TAB_CREATED' as const,
    id,
    workspace,
    tabType,
    tabName,
    insertAtStart,
  };
}
export type WorkspaceTabCreated = ReturnType<typeof workspaceTabCreated>;

///////////////////////////////////////////////////////

export function workspaceTabDeleteSessionRequested(id: string, workspace: WorkspaceType) {
  return {
    type: 'WORKSPACE_TAB_DELETE_SESSION_REQUESTED' as const,
    id,
    workspace,
  };
}
export type WorkspaceTabDeleteSessionRequested = ReturnType<typeof workspaceTabDeleteSessionRequested>;

///////////////////////////////////////////////////////

export function workspaceTabRemoved<W extends WorkspaceType>(id: string, workspace: W) {
  return { type: 'WORKSPACE_TAB_REMOVED' as const, id, workspace };
}
export type WorkspaceTabRemoved = ReturnType<typeof workspaceTabRemoved>;

///////////////////////////////////////////////////////

export function workspaceTabPersistRequested(id: string, workspace: WorkspaceType) {
  return { type: 'WORKSPACE_TAB_PERSIST_REQUESTED' as const, id, workspace };
}
export type WorkspaceTabPersistRequested = ReturnType<typeof workspaceTabPersistRequested>;

///////////////////////////////////////////////////////

export function workspaceTabFrozen(tabId: string, workspace: WorkspaceType, isBusy: true | undefined) {
  return {
    type: 'WORKSPACE_TAB_FROZEN' as const,
    tabId,
    workspace,
    isBusy,
  };
}
export type WorkspaceTabFrozen = ReturnType<typeof workspaceTabFrozen>;

///////////////////////////////////////////////////////

export function workspaceTabSetBusy(tabId: string, workspace: WorkspaceType, isBusy: false | true) {
  return {
    type: 'WORKSPACE_TAB_SET_BUSY' as const,
    tabId,
    workspace,
    isBusy,
  };
}
export type WorkspaceTabSetBusy = ReturnType<typeof workspaceTabSetBusy>;

///////////////////////////////////////////////////////

export function workspaceTabUnfrozen(tabId: string, workspace: WorkspaceType, isBusy: false | undefined) {
  return {
    type: 'WORKSPACE_TAB_UNFROZEN' as const,
    tabId,
    workspace,
    isBusy,
  };
}
export type WorkspaceTabUnfrozen = ReturnType<typeof workspaceTabUnfrozen>;

///////////////////////////////////////////////////////

export function workspaceTabsAllRemoved(workspace: WorkspaceType, tabIds: string[]) {
  return { type: 'WORKSPACE_TABS_ALL_REMOVED' as const, workspace, tabIds };
}
export type WorkspaceTabsAllRemoved = ReturnType<typeof workspaceTabsAllRemoved>;

///////////////////////////////////////////////////////

export function workspaceTabCloned<W extends WorkspaceType>(
  oldTabId: string,
  newTabId: string,
  clonedNegotiationId: string | undefined,
  workspace: W,
  tabType: TabsForWorkspace[W],
  productType: string,
  productSubtype: string,
  wrapper?: string,
) {
  return {
    type: 'WORKSPACE_TAB_CLONED' as const,
    oldTabId,
    newTabId,
    clonedNegotiationId,
    workspace,
    tabType,
    productType,
    productSubtype,
    wrapper,
  };
}
export type WorkspaceTabCloned = ReturnType<typeof workspaceTabCloned>;

///////////////////////////////////////////////////////

export function workspaceTabCloneCompleted<W extends WorkspaceType>(
  sourceTabId: string,
  workspace: W,
  tabType: TabsForWorkspace[W],
) {
  return { type: 'WORKSPACE_TAB_CLONE_COMPLETED' as const, sourceTabId, workspace, tabType };
}
export type WorkspaceTabCloneCompleted = ReturnType<typeof workspaceTabCloneCompleted>;

///////////////////////////////////////////////////////

export function workspaceTabActivated<W extends WorkspaceType>(
  tabId: string,
  workspace: W,
  tabType: TabsForWorkspace[W],
) {
  return { type: 'WORKSPACE_TAB_ACTIVATED' as const, tabId, workspace, tabType };
}
export type WorkspaceTabActivated = ReturnType<typeof workspaceTabActivated>;

///////////////////////////////////////////////////////

export function workspaceTabUnactivated(workspace: WorkspaceType) {
  return { type: 'WORKSPACE_TAB_UNACTIVATED' as const, workspace };
}
export type WorkspaceTabUnactivated = ReturnType<typeof workspaceTabUnactivated>;

///////////////////////////////////////////////////////

export function workspaceTabMoved(workspace: WorkspaceType, sourceIndex: number, targetIndex: number) {
  return { type: 'WORKSPACE_TAB_MOVED' as const, workspace, sourceIndex, targetIndex };
}
export type WorkspaceTabMoved = ReturnType<typeof workspaceTabMoved>;

///////////////////////////////////////////////////////

export function workspaceTabRenamed(id: string, name: string | undefined, workspace: WorkspaceType) {
  return { type: 'WORKSPACE_TAB_RENAMED' as const, id, name, workspace };
}
export type WorkspaceTabRenamed = ReturnType<typeof workspaceTabRenamed>;

///////////////////////////////////////////////////////

export function workspaceResetTabRequested(
  tabId: string,
  workspace: WorkspaceType,
  productType?: string,
  productSubType?: string,
  wrapper?: string,
  selectedOnBehalfClient?: ISalesCounterpart,
) {
  return {
    type: 'WORKSPACE_RESET_TAB_REQUESTED' as const,
    tabId,
    workspace,
    productType,
    productSubType,
    wrapper,
    selectedOnBehalfClient,
  };
}
export type WorkspaceResetTabRequested = ReturnType<typeof workspaceResetTabRequested>;

///////////////////////////////////////////////////////

export function workspaceResetTabCompleted(id: string, newId: string, workspace: WorkspaceType) {
  return { type: 'WORKSPACE_RESET_TAB_COMPLETED' as const, id, newId, workspace };
}
export type WorkspaceResetTabCompleted = ReturnType<typeof workspaceResetTabCompleted>;

///////////////////////////////////////////////////////

export function workspaceTabsFetchRequested(workspace: WorkspaceType, tabFetchRequestId?: string) {
  return { type: 'WORKSPACE_TABS_FETCH_REQUESTED' as const, workspace, tabFetchRequestId };
}
export type WorkspaceTabsFetchRequested = ReturnType<typeof workspaceTabsFetchRequested>;

///////////////////////////////////////////////////////

export function workspaceTabsFetched(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  workspace: WorkspaceType,
  tabFetchRequestId?: string,
  isEssentialTabsPanelShown?: boolean,
) {
  return {
    type: 'WORKSPACE_TABS_FETCHED' as const,
    tabs,
    activeTabId,
    workspace,
    tabFetchRequestId,
    isEssentialTabsPanelShown,
  };
}
export type WorkspaceTabsFetched = ReturnType<typeof workspaceTabsFetched>;

///////////////////////////////////////////////////////

export function workspaceTabsFetchFailed(workspace: WorkspaceType, tabFetchRequestId?: string) {
  return { type: 'WORKSPACE_TABS_FETCH_FAILED' as const, workspace, tabFetchRequestId };
}

export type WorkspaceTabsFetchFailed = ReturnType<typeof workspaceTabsFetchFailed>;

///////////////////////////////////////////////////////

export function workspaceTabsPersisted(tabsDescription: PersistedTabsDescription, workspace: WorkspaceType) {
  return { type: 'WORKSPACE_TABS_PERSISTED' as const, tabsDescription, workspace };
}
export type WorkspaceTabsPersisted = ReturnType<typeof workspaceTabsPersisted>;

///////////////////////////////////////////////////////

export function workspacePersistedTabsApplied(
  tabs: PersistedTab[],
  activeTabId: string | undefined,
  workspace: WorkspaceType,
  isEssentialTabsPanelShown?: boolean,
) {
  return { type: 'WORKSPACE_PERSISTED_TABS_APPLIED' as const, tabs, activeTabId, workspace, isEssentialTabsPanelShown };
}
export type WorkspacePersistedTabsApplied = ReturnType<typeof workspacePersistedTabsApplied>;

///////////////////////////////////////////////////////

export function workspaceTabRepriceRequested(
  id: string,
  idType: IdType,
  rfqId: string,
  pricerRfqId: string | null,
  workspace: WorkspaceType,
  withLastQuote: boolean,
  processingChain?: ProcessingChain,
  externalQuoteId?: string,
  shouldRedirectToWorkspace = true,
  aiGeneratedFields?: string[],
) {
  return {
    type: 'WORKSPACE_TAB_REPRICE_REQUESTED' as const,
    id,
    idType,
    rfqId,
    pricerRfqId,
    workspace,
    externalQuoteId,
    processingChain,
    withLastQuote,
    shouldRedirectToWorkspace,
    aiGeneratedFields,
  };
}
export type WorkspaceTabRepriceRequested = ReturnType<typeof workspaceTabRepriceRequested>;

export function workspaceTabQuoteRepriced(
  tabId: string,
  id: string,
  idType: IdType,
  rfqId: string,
  pricerRfqId: string | null,
  workspace: WorkspaceType,
  withLastQuote: boolean,
  processingChain?: ProcessingChain,
  externalQuoteId?: string,
  aiGeneratedFields?: string[],
) {
  return {
    type: 'WORKSPACE_TAB_QUOTE_REPRICED' as const,
    tabId,
    id,
    idType,
    rfqId,
    pricerRfqId,
    workspace,
    externalQuoteId,
    processingChain,
    withLastQuote,
    aiGeneratedFields,
  };
}
export type WorkspaceTabQuoteRepriced = ReturnType<typeof workspaceTabQuoteRepriced>;

export function workspaceAdvisoryTabCreated<W extends WorkspaceType>(
  id: string,
  workspace: W,
  tabType: TabsForWorkspace[W],
  tabName?: string,
) {
  return { type: 'WORKSPACE_ADVISORY_TAB_CREATED' as const, id, workspace, tabType, tabName };
}
export type WorkspaceAdvisoryTabCreated = ReturnType<typeof workspaceAdvisoryTabCreated>;

export function workspaceQuoteLoaded(
  externalId: string,
  quoteId?: string,
  workspace?: QuoteWorkspaceType,
  source?: Source,
) {
  return {
    type: 'WORKSPACE_QUOTE_LOADED' as const,
    externalId,
    quoteId,
    workspace,
    source,
  };
}
export type WorkspaceQuoteLoaded = ReturnType<typeof workspaceQuoteLoaded>;

export function workspaceRfqLoadRequested(tabId: string, rfqId: string, workspace: WorkspaceType) {
  return {
    type: 'WORKSPACE_RFQ_LOAD_REQUESTED' as const,
    tabId,
    rfqId,
    workspace,
  };
}
export type WorkspaceRfqLoadRequested = ReturnType<typeof workspaceRfqLoadRequested>;

export function workspaceSharableQuoteLoadRequested(rfqId: string) {
  return {
    type: 'WORKSPACE_SHARABLE_QUOTE_LOAD_REQUESTED' as const,
    rfqId,
  };
}
export type WorkspaceSharableQuoteLoadRequested = ReturnType<typeof workspaceSharableQuoteLoadRequested>;

export function workspaceTabProductVariationSelected(
  tabId: string,
  productVariation: Partial<ProductVariation>,
  workspaceType: WorkspaceType,
) {
  return {
    type: 'WORKSPACE_TAB_PRODUCT_VARIATION_SELECTED' as const,
    tabId,
    productVariation,
    workspaceType,
  };
}
export type WorkspaceTabProductVariationSelected = ReturnType<typeof workspaceTabProductVariationSelected>;

export function workspaceTabClientSelected(
  tabId: string,
  client: Nullable<ISalesCounterpart>,
  workspaceType: WorkspaceType,
) {
  return {
    type: 'WORKSPACE_TAB_CLIENT_SELECTED' as const,
    tabId,
    client,
    workspaceType,
  };
}
export type WorkspaceTabClientSelected = ReturnType<typeof workspaceTabClientSelected>;

export function workspaceTabProductSelectionSubmitted(
  tabId: string,
  productVariation: SelectedProductVariation,
  client: Nullable<ISalesCounterpart>,
  workspaceType: WorkspaceType,
) {
  return {
    type: 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED' as const,
    tabId,
    productVariation,
    client,
    workspaceType,
  };
}
export type WorkspaceTabProductSelectionSubmitted = ReturnType<typeof workspaceTabProductSelectionSubmitted>;

export function workspaceTabProductSelectionSubmitFailed(tabId: string) {
  return {
    type: 'WORKSPACE_TAB_PRODUCT_SELECTION_SUBMIT_FAILED' as const,
    tabId,
  };
}
export type WorkspaceTabProductSelectionSubmitFailed = ReturnType<typeof workspaceTabProductSelectionSubmitFailed>;

export function selectedWorkspaceProductSavedToRecentProducts(
  tabId: string,
  productVariation: SelectedProductVariation,
  client: Nullable<ISalesCounterpart>,
  workspaceType: WorkspaceType,
) {
  return {
    type: 'SELECTED_WORKSPACE_PRODUCT_SAVED_TO_RECENT_PRODUCTS' as const,
    tabId,
    productVariation,
    client,
    workspaceType,
  };
}
export type SelectedWorkspaceProductSavedToRecentProducts = ReturnType<
  typeof selectedWorkspaceProductSavedToRecentProducts
>;

export function workspaceQuoteReloadRequested(
  workspace: WorkspaceType,
  temporaryFormId: string,
  identifier: string,
  idType: IdType,
  tradeDate: string,
) {
  return {
    type: 'WORKSPACE_QUOTE_RELOAD_REQUESTED' as const,
    workspace,
    temporaryFormId,
    identifier,
    idType,
    tradeDate,
  };
}
export type WorkspaceQuoteReloadRequested = ReturnType<typeof workspaceQuoteReloadRequested>;

export function workspaceQuoteReloaded(
  workspace: WorkspaceType,
  temporaryFormId: string,
  productVariation?: ProductVariationValidSyntax,
) {
  return {
    type: 'WORKSPACE_QUOTE_RELOADED' as const,
    workspace,
    temporaryFormId,
    productVariation,
  };
}
export type WorkspaceQuoteReloaded = ReturnType<typeof workspaceQuoteReloaded>;
